<template>
  <div class="behaviour-manage-layout py-4">
    <CContainer>
      <CRow v-if="showStudentDetailView" class="align-items-center mb-3">
        <CCol cols="12" md="8">
          <h1 id="behaviour-manage-title" class="h4 mb-0 text-start">
            <button
              type="button"
              class="btn btn-link p-0 text-decoration-none d-inline-flex align-items-center behaviour-manage-student-detail-heading"
              data-testid="behaviour-manage-student-detail-back"
              :aria-label="$t('behaviour_manage.back_to_student_list')"
              @click="clearSelectedStudent"
            >
              <i
                class="ri-arrow-left-s-line behaviour-manage-student-detail-heading__icon"
                aria-hidden="true"
              />
              <span class="ms-1 behaviour-manage-student-detail-heading__name">
                {{ studentDetailDisplayName }}
              </span>
            </button>
          </h1>
        </CCol>
      </CRow>
      <div
        v-if="showManageTabs"
        class="behaviour-manage-tabs d-flex align-items-stretch justify-content-start flex-wrap mb-3"
        role="tablist"
        :aria-label="behaviourTerminology.manageTabsAriaLabel"
      >
        <DashboardTabListItem
          v-for="tab in tabs"
          :key="tab.value"
          :label="tab.label"
          :value="tab.value"
          :tab-element-id="'behaviour-manage-tab-' + tab.value"
          :is-active="activeTab === tab.value"
          :show-check-icon="false"
          @on-tab-click="setActiveTab"
        />
      </div>
      <div
        v-if="activeTab === TAB_POINT_HISTORY || isStudent"
        role="tabpanel"
        id="behaviour-manage-panel-point-history"
        aria-labelledby="behaviour-manage-tab-point-history"
      >
        <router-view />
      </div>
      <div
        v-if="!isStudent && activeTab === TAB_STUDENT_LIST"
        role="tabpanel"
        id="behaviour-manage-panel-student-list"
        aria-labelledby="behaviour-manage-tab-student-list"
      >
        <BehaviourStudentListTab
          :selected-student="selectedStudent"
          @select-student="onStudentSelected"
          @clear-student="clearSelectedStudent"
        />
      </div>
    </CContainer>
  </div>
</template>

<script>
import DashboardTabListItem from "@/v3components/Datatables/DashboardDataTable/TabsAndFilter/DashboardTabListItem.vue"
import BehaviourStudentListTab from "@/v3views/BehaviourStudentListTab.vue"
import { computed, onMounted, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"

const TAB_POINT_HISTORY = "point-history"
const TAB_STUDENT_LIST = "student-list"
const APP_TITLE_SUFFIX = "Securly Pass"

export default {
  name: "BehaviourManageLayout",
  components: {
    DashboardTabListItem,
    BehaviourStudentListTab
  },
  setup() {
    const store = useStore()
    const { t } = useI18n()

    const activeTab = ref(TAB_POINT_HISTORY)
    const selectedStudent = ref(null)

    const behaviourTerminology = computed(
      () => store.getters["behaviourSettings/behaviourTerminology"]
    )

    const userRole = computed(() => store.getters["authentication/getUserRole"])

    const isStudent = computed(() => userRole.value === "student")

    const tabs = computed(() => {
      const bt = behaviourTerminology.value
      const all = [
        {
          label: bt.pointHistoryTab,
          value: TAB_POINT_HISTORY
        },
        {
          label: bt.studentListTab,
          value: TAB_STUDENT_LIST
        }
      ]
      if (isStudent.value) {
        return all.filter((tab) => tab.value === TAB_POINT_HISTORY)
      }
      return all
    })

    const showStudentDetailView = computed(() => Boolean(selectedStudent.value))

    const studentDetailDisplayName = computed(() => {
      const student = selectedStudent.value
      const name = String(student?.studentName ?? "").trim()
      if (name) return name
      const num = String(student?.studentNumber ?? "").trim()
      if (num) return num
      return t("behaviour_manage.col_student")
    })

    const showManageTabs = computed(
      () => tabs.value.length > 1 && !showStudentDetailView.value
    )

    const onStudentSelected = (student) => {
      if (!student?.studentId && !student?.id) return
      selectedStudent.value = { ...student }
      activeTab.value = TAB_STUDENT_LIST
    }

    const clearSelectedStudent = () => {
      selectedStudent.value = null
    }

    const setActiveTab = (value) => {
      if (isStudent.value && value === TAB_STUDENT_LIST) {
        return
      }
      const tab = tabs.value.find((x) => x.value === value)
      if (tab) {
        activeTab.value = value
      }
    }

    const applyDocumentTitle = () => {
      if (typeof document === "undefined") return

      const baseTitle =
        behaviourTerminology.value?.behaviourManagement?.trim?.() || ""
      if (!baseTitle) return

      const alreadyHasSuffix = baseTitle
        .toLowerCase()
        .includes(APP_TITLE_SUFFIX.toLowerCase())
      document.title = alreadyHasSuffix
        ? baseTitle
        : `${baseTitle} - ${APP_TITLE_SUFFIX}`
    }

    watch(
      [tabs, isStudent],
      () => {
        if (isStudent.value && activeTab.value === TAB_STUDENT_LIST) {
          activeTab.value = TAB_POINT_HISTORY
          return
        }
        if (
          tabs.value.length &&
          !tabs.value.some((tab) => tab.value === activeTab.value)
        ) {
          activeTab.value = tabs.value[0]?.value || TAB_POINT_HISTORY
        }
      },
      { immediate: true }
    )

    watch(behaviourTerminology, applyDocumentTitle, { immediate: true })

    onMounted(() => {
      store.dispatch("behaviourSettings/fetchBehaviourTerminology")
      if (!isStudent.value) {
        store.dispatch("passBlocks/getGradeYears")
      }
    })

    return {
      TAB_POINT_HISTORY,
      TAB_STUDENT_LIST,
      isStudent,
      tabs,
      showManageTabs,
      showStudentDetailView,
      studentDetailDisplayName,
      selectedStudent,
      onStudentSelected,
      clearSelectedStudent,
      activeTab,
      setActiveTab,
      behaviourTerminology
    }
  }
}
</script>
